import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import ReportService from '../../services/ReportService';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { AppointmentReportDto, AppointmentReportRequest } from "@ontime/types";
import { DownloadExcel } from './ReportDialog';
import { useSelector } from 'react-redux';
import { selectServiceProviders } from '../../stateManagement/serviceProviderSlice';
import { selectUserOrganizationsList } from '../../stateManagement/organizationUserSlice';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import InfoComponent from '../../customComponents/InfoComponent';
import { AppointmentStatus } from "@ontime/types";

const AppointmentReport: React.FC<{
    loading: boolean;
    setLoading: (input: boolean) => void;
}> = ({ loading, setLoading }) => {

    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<AppointmentReportDto[]>([]);

    const GetAppointmentReport = async (filter: AppointmentReportRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetAppointmentReport(filter);
            setData(response.data.result);
        } catch (error) {
            enqueueSnackbar('Failed to fetch place', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const GetAppointmentReportExcel = async (filter: AppointmentReportRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetAppointmentReportExcel(filter);
            DownloadExcel('AppointmentReport', response.data);
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to fetch Report', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const organizationList = useSelector(selectUserOrganizationsList);
    const serviceProviderList = useSelector(selectServiceProviders);

    const AppointmentByClientIdSchema = z.object({
        organizationId: z.string(),
        startTimeFrom: z.string().optional(),
        startTimeTo: z.string().optional(),
        status: z.nativeEnum(AppointmentStatus).optional(),
        serviceProviderId: z.string().optional(),
    });

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<AppointmentReportRequest>({
        resolver: zodResolver(AppointmentByClientIdSchema),
        defaultValues: {
            organizationId: organizationList?.[0].organizationId ?? 0
        },
    });
    const search = (
        <Box gap={2} m={2}>
            <form onSubmit={handleSubmit(GetAppointmentReport)} noValidate>
                <InfoComponent infoContent='Report filters'>
                    <Box display="flex" justifyContent="space-evenly" flexWrap='wrap' gap={2} mt={2}>
                        <FormControl sx={{ minWidth: '190px' }} error={!!errors.organizationId} disabled={loading}>
                            <InputLabel>Place</InputLabel>
                            <Controller
                                name="organizationId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Place"
                                        {...field}
                                    >
                                        {organizationList.map((item) => (
                                            <MenuItem key={item.organizationId} value={item.organizationId}>
                                                {item.organizationName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors.organizationId?.message}</FormHelperText>
                        </FormControl>
                        <FormControl sx={{ minWidth: '190px' }} error={!!errors.serviceProviderId} disabled={loading}>
                            <InputLabel>service Provider</InputLabel>
                            <Controller
                                name="serviceProviderId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="service Provider"
                                        {...field}
                                    >
                                        <MenuItem value={''}>_</MenuItem>
                                        {serviceProviderList.map((item) => (
                                            <MenuItem key={item.serviceProviderId} value={item.serviceProviderId}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors.serviceProviderId?.message}</FormHelperText>
                        </FormControl>
                        <Controller
                            name="startTimeFrom"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="From"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                        <Controller
                            name="startTimeTo"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="To"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                        <FormControl sx={{ minWidth: '190px' }} error={!!errors.status}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={watch('status')}
                                {...register('status')}
                                onChange={(e) => setValue('status', e.target.value ? (e.target.value as AppointmentStatus) : AppointmentStatus.Confirmed)}
                                disabled={loading}
                            >
                                <MenuItem value={''}>_</MenuItem>
                                <MenuItem value={AppointmentStatus.Pending}>Pending</MenuItem>
                                <MenuItem value={AppointmentStatus.Confirmed}>Confirmed</MenuItem>
                                <MenuItem value={AppointmentStatus.Completed}>Completed</MenuItem>
                                <MenuItem value={AppointmentStatus.Canceled}>Canceled</MenuItem>
                            </Select>
                            <FormHelperText>{errors.status?.message}</FormHelperText>
                        </FormControl>
                    </Box>
                </InfoComponent>
                <CustomFormAction>
                    <CustomButton lable='Show Report' type='submit' disabled={loading} infoContent={`Click to show report`} />
                    <CustomButton lable='Download Excel' onClick={handleSubmit(GetAppointmentReportExcel)} disabled={loading} infoContent={`Click to download excel`} />
                </CustomFormAction>
            </form>
        </Box>
    )

    const appointmentReportType: GridColumnType<AppointmentReportDto> = {
        appointmentStartDate: { type: 'Text', header: 'Start Date' },
        appointmentStartTime: { type: 'Text', header: 'Start Time' },
        appointmentEndDate: { type: 'Text', header: 'End Date' },
        appointmentEndTime: { type: 'Text', header: 'End Time' },
        appointmentAmount: { type: 'Price', header: 'Appointment Amount' },
        client: { type: 'Text', header: 'Client Name' },
        phoneNumber: { type: 'Text', header: 'Phone Number' },
        appointmentStatus: { type: 'Enum', header: 'Status', enumObject: AppointmentStatus },
        serviceProviderPhoneNumber: { type: 'Text', header: 'Service Provider Phone' },
        serviceProviderFullName: { type: 'Text', header: 'Service Provider Name' },
        serviceProviderEmail: { type: 'Text', header: 'Service Provider Email' },
    };

    return (
        <GridTable<AppointmentReportDto, AppointmentReportRequest, AppointmentReportDto>
            gridTitle='Appointment Report'
            list={data}
            inputType={appointmentReportType}
            customSearch={search}
        />
    );
};

export default AppointmentReport;
